import {
	ExtendedListLRSelectItemThird,
	ExtendedLRSelectItem,
	LRSelectItem,
	RadioItem,
	TypedFactory
} from '@/store/prescription/definition/categories/prescriptionCategoriesDefinition';
import {
	Forefoot,
	ForefootPost,
	ForefootPostValue,
	HeelLiftValue,
	PostingOptions,
	RearfootPost,
	RearfootPostUndercut,
	SkiveValue
} from '@/store/prescription/prescription-types';

const post = { '0deg': '0 °', '1deg': '1 °', '2deg': '2 °', '3deg': '3 °', '4deg': '4 °', '5deg': '5 °', '6deg': '6 °' };

const skive = {
	'0mm': 'None',
	'1mm': '1 mm',
	'2mm': '2 mm',
	'3mm': '3 mm',
	'4mm': '4 mm',
	'5mm': '5 mm',
	'6mm': '6 mm',
	'7mm': '7 mm',
	'8mm': '8 mm'
};

const heelLift: LRSelectItem<HeelLiftValue> = {
	LR: {
		'0mm': 'None',
		'1mm': '1 mm',
		'2mm': '2 mm',
		'3mm': '3 mm',
		'4mm': '4 mm',
		'5mm': '5 mm',
		'6mm': '6 mm',
		'7mm': '7 mm',
		'8mm': '8 mm'
	},
	imgPath: '/assets/images/prescriptions/Heel Lift.svg',
	description:
		'Heel Lift will result in added thickness underneath the heel cup area of the orthoses. Heel Lifts are used to address a variety of conditions including, limb length difference, equinus conditions, Achilles tendonitis and plantar fasciitis. They can be used unilaterally or bilaterally and will take up more room in shoes.',
	display: 'Heel Lift'
};

const medialHeelSkive: LRSelectItem<SkiveValue> = {
	LR: { ...skive },
	description:
		'The Medial Heel Skive creates a varus heel post inside the heel cup instead of an extrinsic heel post. We recommend a Deep Heel Seat (5/8”) to accommodate a skive.',
	display: 'Medial Heel Skive',
	imgPath: '/assets/images/prescriptions/Medial Heel Skive.svg'
};

const lateralHeelSkive: LRSelectItem<SkiveValue> = {
	LR: { ...skive },
	description:
		'The Lateral Heel Skive creates a valgus heel post inside the heel cup instead of an extrinsic heel post. We recommend a Deep Heel Seat (5/8”) to accommodate a skive.',
	display: 'Lateral Heel Skive',
	imgPath: '/assets/images/prescriptions/Lateral Heel Skive.svg'
};

const rearfootPost: ExtendedLRSelectItem<RearfootPost['override'], Forefoot['type'], ForefootPostValue> = {
	selectValues: {
		none: 'N/A',
		'lab-specifies': 'Lab selects',
		specify: 'Specify Values'
	},
	LR: {
		firstValue: {
			varus: 'Varus',
			valgus: 'Valgus'
		},
		secondValue: {
			...post
		}
	},
	description:
		'The Rearfoot Post is an extrinsic varus post/wedge where material is added to the inferior surface of the heel at an inverted angle. It is used to stabilize and decelerate the foot and reduce pronation during gait. A rearfoot post will add a small amount of bulk to the device.',
	display: 'Rearfoot Post',
	imgPath: '/assets/images/prescriptions/Varus Rearfoot Post.svg'
};

const forefootPost: ExtendedListLRSelectItemThird<ForefootPost['override'], Forefoot['type'], ForefootPostValue, 'honeycomb' | 'hollow' | 'filled'> = {
	description: `The Forefoot Post tilts either the
medial or lateral front edge of the
orthoses to the ground. Forefoot
Posts can be either Varus or Valgus.
An Intrinsic forefoot post manipulates
the angle of the distal end of the
shell. An Extrinsic forefoot post is
created by adding a wedge under the
distal end of the orthotic shell. All
orthoses made with an extrinsic post
will automatically be balanced
forefoot to rearfoot.`,
	display: 'Forefoot Post',
	imgPath: '/assets/images/prescriptions/Forefoot Post.svg',
	selectValues: {
		none: 'N/A',
		balance: 'Balance to rearfoot',
		extrinsic: 'Extrinsic Specify Values',
		intrinsic: 'Intrinsic Specify Values'
	},
	LR: {
		firstValue: {
			varus: 'Varus',
			valgus: 'Valgus'
		},
		secondValue: {
			...post
		},
		thirdValue: {
			honeycomb: 'Honeycomb',
			hollow: 'Hollow',
			filled: 'Filled'
		}
	}
};

const rearfootPostUndercut: RadioItem<RearfootPostUndercut> = {
	description:
		'Adjusting the undercut can help with improving shoe fit and/or improving stability. The default is 15 degree undercut of the heel post. For improved stability, select the Wide undercut which widens the heel post. For improved shoe fit of narrow shoes, select Narrow.',
	display: 'Rearfoot Post Undercut',
	imgPath: '/assets/images/prescriptions/Rearfoot Undercut.svg',
	radioValues: {
		narrow: 'Narrow 20°',
		standard: 'Standard 15°',
		wide: 'Wide 10°'
	}
};

const typeFactory = new TypedFactory<PostingOptions>();
export default typeFactory.typeItems({
	heelLift,
	medialHeelSkive,
	lateralHeelSkive,
	rearfootPost,
	forefootPost,
	rearfootPostUndercut
});
